import { useState } from 'react';
import { Plus, Truck } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { EmptyState } from '../../components/common/EmptyState';
import { VEHICLE_MAKES, VEHICLE_COLORS, getVehicleYears, MODEL_PLACEHOLDERS, VEHICLE_TYPES } from '../../data/vehicleData';
import { VEHICLE_STATUS, TH, TD, LABEL, INPUT } from './config';
import type { Vehicle } from '../../types';

interface Props {
  vehicles: Vehicle[];
  onCreate: (v: Partial<Vehicle>, userId: string) => Promise<boolean>;
  onUpdate: (id: string, v: Partial<Vehicle>, userId: string) => Promise<boolean>;
  onRetire: (id: string, name: string, userId: string) => Promise<boolean>;
  userId: string;
}

const YEARS = getVehicleYears();

export function VehiclesTab({ vehicles, onCreate, onUpdate, onRetire, userId }: Props) {
  const [showAdd, setShowAdd] = useState(false);
  const [editV, setEditV] = useState<Vehicle | null>(null);
  const [confirmRetire, setConfirmRetire] = useState<Vehicle | null>(null);
  const active = vehicles.filter(v => v.status !== 'retired');
  const retired = vehicles.filter(v => v.status === 'retired');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: palette.g500 }}>{active.length} vehicle{active.length !== 1 ? 's' : ''}</span>
        <button onClick={() => setShowAdd(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: 'none', background: palette.accent, fontSize: 12.5, fontWeight: 600, color: palette.white, cursor: 'pointer', fontFamily: 'inherit' }}><Plus size={14} /> Add Vehicle</button>
      </div>

      {active.length === 0 ? (
        <EmptyState icon={<Truck size={22} />} title="No vehicles yet" subtitle="Add your first vehicle to track trips and maintenance." actionLabel="Add Vehicle" onAction={() => setShowAdd(true)} />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={TH}>Name</th><th style={TH}>Make / Model / Year</th><th style={TH}>Plate</th>
            <th style={TH}>Status</th><th style={TH}>Odometer</th><th style={TH}>Fuel</th><th style={TH}>Next Maint.</th>
          </tr></thead>
          <tbody>
            {active.map(v => {
              const s = VEHICLE_STATUS[v.status] ?? VEHICLE_STATUS.available;
              return (
                <tr key={v.id} onClick={() => setEditV(v)} style={{ cursor: 'pointer', transition: 'background 150ms ease' }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ ...TD, fontWeight: 600, color: palette.g800 }}>{v.name}</td>
                  <td style={TD}>{[v.make, v.model, v.year].filter(Boolean).join(' ') || '—'}</td>
                  <td style={{ ...TD, fontFamily: 'monospace' }}>{v.plate ?? '—'}</td>
                  <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: s.bg, color: s.fg }}>{s.label}</span></td>
                  <td style={TD}>{v.odometer ? `${v.odometer.toLocaleString()} km` : '—'}</td>
                  <td style={TD}>{v.fuel_level != null ? `${v.fuel_level}%` : '—'}</td>
                  <td style={TD}>{v.next_maintenance_date ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {retired.length > 0 && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ fontSize: 12, color: palette.g400, cursor: 'pointer' }}>{retired.length} retired vehicle{retired.length > 1 ? 's' : ''}</summary>
          <div style={{ marginTop: 8, opacity: 0.6 }}>
            {retired.map(v => <div key={v.id} style={{ fontSize: 13, color: palette.g500, padding: '4px 0' }}>{v.name} ({v.plate ?? 'no plate'})</div>)}
          </div>
        </details>
      )}

      <VehicleForm isOpen={showAdd} onClose={() => setShowAdd(false)} onSave={async v => { const ok = await onCreate(v, userId); if (ok) setShowAdd(false); return ok; }} title="Add Vehicle" />
      <VehicleForm isOpen={!!editV} vehicle={editV ?? undefined} onClose={() => setEditV(null)}
        onSave={async v => { if (!editV) return false; const ok = await onUpdate(editV.id, v, userId); if (ok) setEditV(null); return ok; }}
        title="Edit Vehicle" onRetire={editV ? () => setConfirmRetire(editV) : undefined} />
      <ConfirmDialog isOpen={!!confirmRetire} title={`Retire ${confirmRetire?.name}?`} message="Vehicle will be hidden from active lists." confirmLabel="Retire"
        onConfirm={async () => { if (confirmRetire) { await onRetire(confirmRetire.id, confirmRetire.name, userId); setConfirmRetire(null); setEditV(null); } }}
        onClose={() => setConfirmRetire(null)} />
    </div>
  );
}

function VehicleForm({ isOpen, vehicle, onClose, onSave, title, onRetire }: {
  isOpen: boolean; vehicle?: Vehicle; onClose: () => void;
  onSave: (v: Partial<Vehicle>) => Promise<boolean>; title: string; onRetire?: () => void;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [plate, setPlate] = useState('');
  const [color, setColor] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset on open
  if (isOpen && vehicle && name === '' && make === '') {
    setName(vehicle.name); setType(vehicle.type ?? ''); setMake(vehicle.make ?? '');
    setModel(vehicle.model ?? ''); setYear(vehicle.year ? String(vehicle.year) : '');
    setPlate(vehicle.plate ?? ''); setColor(vehicle.color ?? ''); setNotes(vehicle.notes ?? '');
  }

  const handleClose = () => { setName(''); setType(''); setMake(''); setModel(''); setYear(''); setPlate(''); setColor(''); setNotes(''); onClose(); };

  const handleSave = async () => {
    setSaving(true);
    await onSave({ name, type: type || null, make: make || null, model: model || null, year: year ? Number(year) : null, plate: plate || null, color: color || null, notes: notes || null });
    setSaving(false);
  };

  const F: React.CSSProperties = { marginBottom: 14 };
  const placeholder = MODEL_PLACEHOLDERS[make] ?? 'Model name';

  return (
    <SlidePanel isOpen={isOpen} onClose={handleClose} title={title}>
      <div style={F}><label style={LABEL}>Name *</label><input style={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Work Truck #1" /></div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Year</label><select style={INPUT} value={year} onChange={e => setYear(e.target.value)}><option value="">—</option>{YEARS.map(y => <option key={y}>{y}</option>)}</select></div>
        <div style={{ flex: 1 }}><label style={LABEL}>Type</label><select style={INPUT} value={type} onChange={e => setType(e.target.value)}><option value="">—</option>{VEHICLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
      </div>
      <div style={F}><label style={LABEL}>Make</label><select style={INPUT} value={make} onChange={e => setMake(e.target.value)}><option value="">—</option>{VEHICLE_MAKES.map(m => <option key={m}>{m}</option>)}</select></div>
      <div style={F}><label style={LABEL}>Model</label><input style={INPUT} value={model} onChange={e => setModel(e.target.value)} placeholder={placeholder} /></div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Plate</label><input style={INPUT} value={plate} onChange={e => setPlate(e.target.value)} /></div>
        <div style={{ flex: 1 }}><label style={LABEL}>Color</label><select style={INPUT} value={color} onChange={e => setColor(e.target.value)}><option value="">—</option>{VEHICLE_COLORS.map(c => <option key={c}>{c}</option>)}</select></div>
      </div>
      <div style={F}><label style={LABEL}>Notes</label><textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} /></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        {onRetire ? <button onClick={onRetire} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: 'transparent', fontSize: 13, color: palette.red, cursor: 'pointer', fontFamily: 'inherit' }}>Retire</button> : <div />}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !name.trim()} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving || !name.trim() ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </SlidePanel>
  );
}
